import { useEffect, useState, useId } from 'react';
import { SectionBlock as ISectionBlock } from '@/lib/types';
import {
  HStack,
  Box,
  Button,
  Field,
  Icon,
  Input,
  VStack,
  Separator,
  AbsoluteCenter,
  Stack,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import TextEditor from '@/components/ui/text-editor';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';
import { useFillableBlockResponse } from './common';
import GroupBlock from './section-blocks/group-block';
import api from '@/utils/api';
import { ContentType, Content, SectionForm } from '@/lib/types';

interface Props {
  block: ISectionBlock;
}

const initialSection = {
  name: '',
  duration: '',
  notes: '',
  groups: [],
};

export default function SectionBlock({ block }: Props) {
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const [contents, setContents] = useState<Content[]>([]);
  const { payload, updatePayload } = useFillableBlockResponse({ block });
  const [form, setForm] = useState<SectionForm[]>(payload?.value || []);
  const { workspaceId } = useParams();

  const keyId = useId();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentTypesResponse, contentsResponse] = await Promise.all([
          api.get(`content-types/workspace/${workspaceId}`),
          api.get(`contents/workspace/${workspaceId}`),
        ]);

        if (contentTypesResponse.data.data) {
          setContentTypes(contentTypesResponse.data.data);
        }

        if (contentsResponse.data.data) {
          setContents(contentsResponse.data.data);
        }
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to fetch content',
        });
      }
    };

    fetchData();
  }, [workspaceId]);

  useEffect(() => {
    if (payload?.value) {
      setForm(payload.value);
    }
  }, [payload]);

  function applyChanges(updatedForm: SectionForm, index: number) {
    const newForm = form.map((item, i) => (i === index ? updatedForm : item));
    setForm(newForm);
    updatePayload({ value: newForm });
  }

  function addGroup(row: SectionForm, index: number) {
    const updatedRow = {
      ...row,
      groups: [
        ...row.groups,
        {
          name: '',
          contents: [],
        },
      ],
    };
    applyChanges(updatedRow, index);
  }

  return (
    <Box w={'full'}>
      <Box position={'relative'} py={4} w={'full'} mb={3}>
        <Separator borderColor='gray.200' />
        <AbsoluteCenter bg={'white'} px={'4'}>
          Sections
        </AbsoluteCenter>
      </Box>
      {form.map((row, i) => (
        <VStack key={`${keyId}-items-${i}`} gap={4} align={'stretch'}>
          <VStack align={'stretch'} gap={4}>
            <HStack gap={4}>
              <Box>{i + 1}.</Box>
              <Field.Root>
                <Input
                  id={`section-name-${i}`}
                  value={row.name}
                  placeholder={'Name this section...'}
                  onChange={(e) => {
                    applyChanges({ ...row, name: e.currentTarget.value }, i);
                  }}
                />
              </Field.Root>
            </HStack>
            <Field.Root pl={{ base: 10, md: 14 }}>
              <Field.Label>Duration</Field.Label>
              <Input
                id={`duration-${i}`}
                type={'number'}
                value={row.duration === null ? '' : row.duration}
                placeholder={'Duration...'}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  applyChanges({ ...row, duration: value === '' ? null : Number(value) }, i);
                }}
              />
            </Field.Root>
            <Field.Root pl={{ base: 10, md: 14 }}>
              <Field.Label>Notes</Field.Label>
              <TextEditor
                initialValue={row.notes}
                placeholder={'Add notes...'}
                onChange={(newContent) =>
                  applyChanges(
                    {
                      ...row,
                      notes: newContent,
                    },
                    i,
                  )
                }
              />
            </Field.Root>
            {workspaceId && (
              <VStack pl={{ base: 12, md: 20 }} gap={4}>
                <GroupBlock
                  index={i}
                  item={row}
                  workspaceId={parseInt(workspaceId, 10)}
                  contentTypes={contentTypes}
                  contents={contents}
                  onChange={applyChanges}
                />
              </VStack>
            )}
            <Stack
              pl={{ base: 10, md: 14 }}
              direction={{ base: 'column', md: 'row' }}
              gap={3}
              align={{ base: 'left', md: 'center' }}
            >
              <Button
                variant={'surface'}
                size={'sm'}
                onClick={() => {
                  addGroup(row, i);
                }}
              >
                <Icon as={PlusIcon} w={4} h={4} />
                Add Group
              </Button>
              <Button
                variant={'surface'}
                size={'sm'}
                onClick={() => {
                  const formCopy = [...form];
                  formCopy.splice(i, 1);
                  setForm(formCopy);
                  updatePayload({ value: formCopy });
                }}
              >
                <Icon as={TrashIcon} w={3} h={3} />
                Delete Section
              </Button>
            </Stack>
            <Separator borderColor={'gray.200'} mb={6} />
          </VStack>
        </VStack>
      ))}
      <Button variant='surface' w={'full'} onClick={() => setForm([...form, initialSection])}>
        <Icon as={PlusIcon} w={4} h={4} />
        Add Section
      </Button>
    </Box>
  );
}
